import { useContext, useRef, useState } from 'react';

import * as Yup from 'yup';
import {
  AbortableFetch,
  FormHandle,
  SnackbarContext,
  SnackbarVariant,
  useEventCallback,
} from '@eas/common-web';
import { Mpt } from '../../models';
import { MptFormMode, uploadMPT } from './mpt-api';
import { Messages } from '@enums';

export function useMpt() {
  const ref = useRef<FormHandle<Mpt>>(null);
  const fetchRef = useRef<null | AbortableFetch>(null);

  const [formMode, setFormMode] = useState(MptFormMode.DEFAULT);

  const { showSnackbar } = useContext(SnackbarContext);

  const validationSchema = Yup.object<Mpt>().shape({
    fileCz: Yup.string().nullable().required(),
    fileImages: Yup.string().nullable().required(),
  });

  const handleStartEditing = useEventCallback(() => {
    setFormMode(MptFormMode.EDITING);
  });

  const handleCancelEditing = useEventCallback(() => {
    setFormMode(MptFormMode.DEFAULT);
    ref.current?.clearForm();
    ref.current?.resetValidation();
  });

  const handleCancelLoading = () => {
    fetchRef.current?.abort();
    setFormMode(MptFormMode.EDITING);
  };

  const loading = formMode === MptFormMode.LOADING;
  const editing = formMode === MptFormMode.EDITING;

  const handleSubmit = useEventCallback(async () => {
    if (ref.current != undefined) {
      try {
        if (fetchRef.current !== null) {
          fetchRef.current.abort();
        }
        setFormMode(MptFormMode.LOADING);

        const errors = await ref.current.validateForm();

        if (errors.length > 0) {
          const message = Messages.Common.VALIDATION_ERROR;
          showSnackbar(...message);
          setFormMode(MptFormMode.EDITING);
          return;
        }

        const values = ref.current.getFieldValues();
        const formData = new FormData();

        Object.entries(values).forEach(([key, value]) =>
          formData.append(key, value)
        );

        fetchRef.current = uploadMPT(formData);

        const timestamp = await fetchRef.current.json();

        ref.current.clearForm();
        ref.current.setFieldValue('timestamp', timestamp);

        const message = Messages.Common.SUCCESS;

        showSnackbar(...message, true);
        setFormMode(MptFormMode.DEFAULT);
      } catch (e: any) {
        if (e.name === 'AbortError') {
          const message = 'Akce zrušena uživatelem.';

          showSnackbar(message, SnackbarVariant.ERROR, true);
        } else {
          const message = Messages.Common.ERROR;

          showSnackbar(...message);
        }
        setFormMode(MptFormMode.EDITING);
      }
    }
  });

  return {
    loading,
    editing,
    handleSubmit,
    handleStartEditing,
    handleCancelEditing,
    handleCancelLoading,
    ref,
    validationSchema,
  };
}
